import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import LockIcon from "@mui/icons-material/Lock";
import { FUNDING_MODEL_DESCRIPTION } from "@/constants/review";

export type FundingModel = "prepaid" | "payg" | "postpaid";

const OPTIONS: { key: FundingModel; label: string }[] = [
  { key: "prepaid", label: FUNDING_MODEL_NAME.prepaid },
  { key: "payg", label: FUNDING_MODEL_NAME.automated },
  { key: "postpaid", label: FUNDING_MODEL_NAME.postpaid },
];

/**
 * "How you'll pay" — Prepaid · Pay-as-you-give · Postpaid, single-select because
 * `campaigns.campaign_type` holds one value.
 *
 * Rendered ONLY where there is a real three-way choice: an organisation approved for postpaid. Every
 * other client keeps `PaygRow` exactly as it is, UPGRADE badge and all — this control never appears
 * with an option greyed out that the client could not have had.
 *
 * Like `PaygRow`, it reports every selection and lets the checkout column decline it: a campaign
 * under the pay-as-you-give minimum, or one whose postpaid ceiling does not fit under the credit
 * limit, comes back as a `notice` with `value` unchanged.
 */
export interface FundingModelControlProps {
  value: FundingModel;
  onChange: (next: FundingModel) => void;
  /** The plan does not include pay-as-you-give — that option routes to the upgrade flow. */
  paygLocked: boolean;
  onUpgrade: () => void;
  /** Options shown but not selectable — Postpaid when its ceiling will not fit under the limit. */
  disabled?: FundingModel[];
  notice?: string | null;
}

export default function FundingModelControl({
  value,
  onChange,
  paygLocked,
  onUpgrade,
  disabled = [],
  notice,
}: FundingModelControlProps) {
  return (
    <div className="mt-4">
      <div className="mb-2 font-lato text-[13px] font-semibold text-rodeo-black">
        How you'll pay
      </div>
      <fieldset className="m-0 grid grid-cols-3 gap-1 rounded-card border-0 bg-light-dust p-1">
        <legend className="sr-only">How you'll pay</legend>
        {OPTIONS.map((o) => {
          const selected = value === o.key;
          const locked = o.key === "payg" && paygLocked;
          const off = disabled.includes(o.key);
          return (
            <label
              key={o.key}
              className={`inline-flex items-center justify-center gap-1 rounded-btn px-2 py-2.5 font-lato text-[12.5px] transition-colors has-focus-visible:outline-2 ${
                selected
                  ? "bg-white font-bold text-rodeo-black shadow-[0_1px_2px_rgba(0,0,0,.08)]"
                  : off
                    ? "cursor-not-allowed text-graphite opacity-45"
                    : "cursor-pointer text-graphite hover:bg-white/60"
              }`}
            >
              <input
                type="radio"
                name="funding-model"
                className="sr-only"
                checked={selected}
                disabled={off}
                onChange={() => (locked ? onUpgrade() : onChange(o.key))}
              />
              {locked && <LockIcon sx={{ fontSize: 12 }} aria-hidden="true" />}
              {o.label}
            </label>
          );
        })}
      </fieldset>
      <p className="mt-2 mb-0 font-lato text-[12px] leading-[1.45] text-graphite">
        {FUNDING_MODEL_DESCRIPTION[value]}
      </p>
      {notice && (
        <p
          role="alert"
          className="mt-2 mb-0 font-lato text-[12px] font-semibold text-good-wine leading-[1.4]"
        >
          {notice}
        </p>
      )}
    </div>
  );
}
